import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Shield, RefreshCw, Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const donationTiers = [
  { amount: 50, label: '$50', description: 'Provides basic athletic equipment for one child', popular: false },
  { amount: 250, label: '$250', description: 'Covers league registration for one season', popular: true },
  { amount: 175, label: '$175', description: 'Funds training program access for a month', popular: false },
  { amount: 500, label: '$500', description: 'Sponsors a child athlete for an entire season', popular: false },
];

const Donate = () => {
  const [searchParams] = useSearchParams();
  const [selectedAmount, setSelectedAmount] = useState(250);
  const [customAmount, setCustomAmount] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  // Check if returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/donations/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      setPaymentStatus('processing');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setPaymentStatus('error');
    }
  };

  const handleSelectAmount = (amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  };

  const handleCustomAmount = (value) => {
    setCustomAmount(value);
    setIsCustom(true);
    setSelectedAmount(0);
  };

  const getFinalAmount = () => {
    if (isCustom && customAmount) {
      return parseFloat(customAmount);
    }
    return selectedAmount;
  };

  const handleDonate = async () => {
    const amount = getFinalAmount();
    if (!amount || amount <= 0) {
      setError('Please select or enter a donation amount');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/donations/checkout`, {
        amount: parseFloat(amount.toFixed(2)),
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        is_recurring: isRecurring,
        origin_url: window.location.origin,
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err) {
      console.error('Donation error:', err);
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  // Success/Error states
  if (paymentStatus === 'success') {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center" data-testid="donation-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg mx-auto text-center p-12 bg-white shadow-xl"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Thank You!</h1>
          <p className="mt-4 text-lg text-slate-600">
            Your donation has been successfully processed. Together, we're helping young athletes reach their potential.
          </p>
          <Link to="/" className="inline-block mt-8">
            <Button className="bg-slate-900 text-white hover:bg-red-600 rounded-none uppercase tracking-widest font-bold px-8 py-4">
              Return Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  if (paymentStatus === 'processing') {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-red-600 animate-spin mx-auto" />
          <p className="mt-4 text-lg text-slate-600">Processing your donation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="donate-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Heart className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold">
              Make a Difference Today
            </h1>
            <p className="mt-6 text-xl text-slate-300 max-w-2xl mx-auto">
              Your donation helps remove financial barriers and gives young athletes the opportunity to compete and thrive.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Donation Form Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="donation-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Amount Selection */}
            <div className="lg:col-span-3">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Select Donation Amount</h2>
              
              {/* Tier Cards */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {donationTiers.map((tier) => (
                  <button
                    key={tier.amount}
                    onClick={() => handleSelectAmount(tier.amount)}
                    className={`donation-tier p-6 border-2 text-left transition-all ${
                      selectedAmount === tier.amount && !isCustom
                        ? 'border-red-600 bg-red-50'
                        : 'border-slate-200 hover:border-slate-300'
                    } ${tier.popular ? 'popular' : ''}`}
                    data-testid={`donation-tier-${tier.amount}`}
                  >
                    <span className="font-heading text-2xl font-bold text-slate-900">{tier.label}</span>
                    <p className="mt-2 text-sm text-slate-600">{tier.description}</p>
                  </button>
                ))}
              </div>

              {/* Custom Amount */}
              <div className="mb-8">
                <Label className="text-sm font-medium text-slate-700">Or enter a custom amount</Label>
                <div className="mt-2 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => handleCustomAmount(e.target.value)}
                    className="pl-8 py-6 text-lg border-slate-200"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Recurring Option */}
              <div className="flex items-center space-x-3 p-4 bg-slate-50 border border-slate-200 mb-8">
                <Checkbox
                  id="recurring"
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                  data-testid="recurring-checkbox"
                />
                <div className="flex-1">
                  <Label htmlFor="recurring" className="font-medium text-slate-900 cursor-pointer">
                    Make this a monthly donation
                  </Label>
                  <p className="text-sm text-slate-600">Support athletes consistently with recurring giving</p>
                </div>
                <RefreshCw className={`w-5 h-5 ${isRecurring ? 'text-red-600' : 'text-slate-400'}`} />
              </div>

              {/* Donor Information */}
              <div className="space-y-4 mb-8">
                <h3 className="font-heading text-lg font-bold text-slate-900">Your Information (Optional)</h3>
                <div>
                  <Label className="text-sm text-slate-700">Name</Label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    className="mt-1 border-slate-200"
                    data-testid="donor-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm text-slate-700">Email</Label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    className="mt-1 border-slate-200"
                    data-testid="donor-email-input"
                  />
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handleDonate}
                disabled={isLoading}
                className="w-full bg-red-600 text-white hover:bg-slate-900 rounded-none uppercase tracking-widest font-bold py-6 text-lg"
                data-testid="donate-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    Donate ${getFinalAmount().toFixed(2)}
                    {isRecurring && '/month'}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2">
              {/* Security Notice */}
              <div className="bg-slate-50 p-6 border border-slate-200 mb-6">
                <div className="flex items-center space-x-3 mb-4">
                  <Shield className="w-6 h-6 text-green-600" />
                  <span className="font-bold text-slate-900">Secure Donation</span>
                </div>
                <p className="text-sm text-slate-600">
                  Your payment is processed securely through Stripe. We never store your card details.
                </p>
              </div>

              {/* Tax Notice */}
              <div className="bg-amber-50 p-6 border border-amber-200">
                <h4 className="font-bold text-amber-900 mb-2">Tax Deductibility Notice</h4>
                <p className="text-sm text-amber-800">
                  Lane 4 For All Kids Foundation is currently in the process of obtaining 501(c)(3) tax-exempt status. Donations may not yet be tax-deductible.
                </p>
              </div>

              {/* Impact Summary */}
              <div className="mt-6 p-6 bg-red-600 text-white">
                <h4 className="font-heading text-lg font-bold mb-4">Your Impact</h4>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>100% goes directly to supporting young athletes</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Equipment, registration, and training programs</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Quarterly impact reports for monthly donors</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Ways to Give */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl font-bold text-slate-900">Other Ways to Support</h2>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto">
            Beyond monetary donations, there are many ways to help young athletes succeed.
          </p>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            <Link to="/sponsors" className="p-8 bg-white border border-slate-200 hover:shadow-lg transition-shadow" data-testid="sponsor-link">
              <h3 className="font-heading text-xl font-bold text-slate-900">Corporate Sponsorship</h3>
              <p className="mt-2 text-slate-600">Partner with us as a corporate sponsor starting at $1,000</p>
            </Link>
            <Link to="/get-involved" className="p-8 bg-white border border-slate-200 hover:shadow-lg transition-shadow" data-testid="volunteer-link">
              <h3 className="font-heading text-xl font-bold text-slate-900">Volunteer</h3>
              <p className="mt-2 text-slate-600">Give your time and skills to support our programs</p>
            </Link>
            <Link to="/contact" className="p-8 bg-white border border-slate-200 hover:shadow-lg transition-shadow" data-testid="equipment-link">
              <h3 className="font-heading text-xl font-bold text-slate-900">Equipment Donation</h3>
              <p className="mt-2 text-slate-600">Donate gently used athletic equipment</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
